import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentElementForm } from "@/components/PaymentElementForm";
import { HEATHER_IMG, HEATHER_NAME, HEATHER_TITLE, STORE_ADDRESS, STORE_PHONE, STORE_HOURS } from "@/lib/brand";
import { X, Check, ArrowLeft, ArrowRight, Scissors, MapPin, Phone, Clock, ShieldCheck, Sparkles, Lock, Package, HeartHandshake } from "lucide-react";
import { mpInitiateCheckout } from "@/lib/metaPixel";

const SEAMSTRESS_FEE = 299;
const GARMENT_BAG_FEE = 35;
const STEPS = ["Your Bag", "Details", "Secure"];

export function CartLightbox({ open, onClose, cart, firstName = "there", schoolName, onSuccess, onSchoolVerifyRequired }) {
  const [step, setStep] = useState(0);
  const [seamstress, setSeamstress] = useState(false);
  const [appointment, setAppointment] = useState("");
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(false);
  const [extras, setExtras] = useState({ garment_bag: false, donation: 0, donation_school_id: null });
  const navigate = useNavigate();

  // Load add-ons (garment bag, donation) chosen in the SideCart
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem("hb_cart_extras");
      if (raw) setExtras({ garment_bag: false, donation: 0, donation_school_id: null, ...JSON.parse(raw) });
    } catch { /* ignore */ }
    // Meta Pixel — InitiateCheckout
    try {
      const items = (cart?.items || []).map((it) => ({
        id: it.color ? `${it.product_id}:${it.color}` : it.product_id,
        quantity: it.quantity || 1,
        item_price: it.line_total && it.quantity ? it.line_total / it.quantity : 0,
      }));
      const num = (cart?.items || []).reduce((n, i) => n + (i.quantity || 1), 0);
      mpInitiateCheckout({ items, value: cart?.subtotal || 0, num_items: num, kind: "cart" });
    } catch { /* noop */ }
  }, [open]);

  if (!open || !cart?.items?.length) return null;
  const close = () => { setPaying(false); setStep(0); onClose(); };
  const first = cart.items[0];
  const heroImg = first.image || first.product?.images?.[0];
  const donationAmt = Number(extras.donation) || 0;
  const bagFee = extras.garment_bag ? GARMENT_BAG_FEE : 0;
  const total = cart.subtotal + (seamstress ? SEAMSTRESS_FEE : 0) + bagFee + donationAmt;
  const count = cart.items.reduce((n, i) => n + i.quantity, 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6" onClick={close}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" />
      <div data-testid="cart-lightbox" className="relative bg-ivory rounded-sm w-full max-w-5xl max-h-[95vh] grid md:grid-cols-[0.85fr_1.15fr] overflow-hidden shadow-2xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} data-testid="cart-lightbox-close" className="absolute top-4 right-4 z-30 w-8 h-8 rounded-full bg-ink/60 text-ivory flex items-center justify-center hover:bg-ink"><X className="w-4 h-4" /></button>

        {/* LEFT — visual */}
        <div className="relative hidden md:flex flex-col" style={{ background: "linear-gradient(160deg,#f6ecec 0%,#efe6f0 55%,#e7dced 100%)" }}>
          <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 glass-dark text-ivory font-label text-[9px] px-2.5 py-1 rounded-full"><Sparkles className="w-3 h-3 text-champagne" /> Heather Approved</span>
            <span className="font-label text-[9px] text-ink/70 border border-ink/20 bg-ivory/60 px-2.5 py-1 rounded-full">{count} {count === 1 ? "piece" : "pieces"}</span>
          </div>
          <div className="relative flex-1 min-h-0 flex items-end justify-center">
            <img src={heroImg} alt={first.product?.name} className="max-h-full w-auto object-contain object-bottom" />
          </div>
          <div className="relative p-6 bg-gradient-to-t from-ink via-ink/95 to-transparent text-ivory">
            <p className="font-label text-[9px] text-champagne mb-1">Your Private Styling</p>
            <h4 className="font-display text-2xl leading-tight mb-3">{count === 1 ? first.product?.name : `${count} pieces, hand-checked by Heather`}</h4>
            <div className="flex items-center gap-3 mb-3">
              <img src={HEATHER_IMG} alt={HEATHER_NAME} className="w-10 h-10 rounded-full object-cover object-top border border-champagne/50" />
              <div><div className="font-body text-sm">{HEATHER_NAME}</div><div className="font-label text-[8px] text-ivory/60">{HEATHER_TITLE}</div></div>
            </div>
            <div className="space-y-1 font-body text-[11px] text-ivory/70">
              <p className="flex items-center gap-2"><MapPin className="w-3 h-3 text-champagne" /> {STORE_ADDRESS}</p>
              <p className="flex items-center gap-2"><Phone className="w-3 h-3 text-champagne" /> {STORE_PHONE}</p>
              <p className="flex items-center gap-2"><Clock className="w-3 h-3 text-champagne" /> {STORE_HOURS}</p>
            </div>
          </div>
        </div>

        {/* RIGHT — flow */}
        <div className="flex flex-col max-h-[95vh] overflow-y-auto">
          <div className="flex items-center gap-2 px-6 sm:px-8 pt-7">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-label ${i < step ? "bg-rosemetal text-ivory" : i === step ? "bg-ink text-ivory" : "bg-border text-muted-foreground"}`}>{i < step ? <Check className="w-3 h-3" /> : i + 1}</span>
                <span className={`font-label text-[9px] uppercase ${i === step ? "text-ink" : "text-muted-foreground"}`}>{s}</span>
                {i < STEPS.length - 1 && <span className="w-4 sm:w-6 h-px bg-border" />}
              </div>
            ))}
          </div>

          <div className="px-6 sm:px-8 py-6 flex-1">
            {/* STEP 0 — BAG */}
            {step === 0 && (
              <div data-testid="cart-step-bag" className="space-y-5">
                <div className="flex gap-3 items-start bg-blush/50 rounded-sm p-4">
                  <img src={HEATHER_IMG} alt={HEATHER_NAME} className="w-12 h-12 rounded-full object-cover object-top shrink-0" />
                  <div>
                    <p className="font-label text-[9px] text-rosemetal mb-1">A note from Heather</p>
                    <p className="font-display text-lg leading-snug italic">"{firstName}, every piece in your bag has my eyes on it. Let's make this official — I'll have everything perfect for your night."</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {cart.items.map((it, idx) => (
                    <div key={idx} data-testid={`cart-lightbox-item-${idx}`} className="flex items-center gap-3 border border-border rounded-sm p-3">
                      <div className="w-14 shrink-0 rounded-sm overflow-hidden bg-blush"><div className="aspect-[3/4]"><img src={it.image || it.product?.images?.[0]} alt={it.product?.name} className="w-full h-full object-cover object-top" /></div></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-base leading-tight truncate">{it.product?.name}</p>
                        <p className="font-label text-[8px] text-muted-foreground mt-0.5">{[it.color, it.size ? `Size ${it.size}` : null, it.quantity > 1 ? `×${it.quantity}` : null].filter(Boolean).join(" · ") || "One size"}</p>
                      </div>
                      <span className="font-body text-sm">${it.line_total}</span>
                    </div>
                  ))}
                </div>

                <button data-testid="cart-seamstress-toggle" onClick={() => setSeamstress((v) => !v)} className={`w-full text-left border rounded-sm p-4 transition-colors ${seamstress ? "border-rosemetal bg-blush/50" : "border-border hover:border-ink"}`}>
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${seamstress ? "border-rosemetal bg-rosemetal" : "border-border"}`}>{seamstress && <Check className="w-3 h-3 text-ivory" />}</span>
                    <div>
                      <div className="font-label text-[8px] text-rosemetal mb-0.5">Optional upgrade · +${SEAMSTRESS_FEE} · unlocks in-boutique fitting</div>
                      <div className="font-display text-lg flex items-center gap-2"><Scissors className="w-4 h-4" /> Master Seamstress Styling Suite</div>
                      <p className="font-body text-xs text-muted-foreground mt-1">A private fitting & alterations with {HEATHER_NAME} herself — your look, perfected in the boutique.</p>
                    </div>
                  </div>
                </button>

                <button data-testid="cart-bag-continue" onClick={() => setStep(1)} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2">Continue <ArrowRight className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {/* STEP 1 — DETAILS */}
            {step === 1 && (
              <div data-testid="cart-step-details" className="space-y-5">
                <div>
                  <p className="font-label text-[9px] text-rosemetal mb-1">Almost there</p>
                  <h3 className="font-display text-3xl">Your details, {firstName}</h3>
                </div>
                {seamstress && (
                  <div>
                    <div className="font-label text-[9px] text-muted-foreground mb-2 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-rosemetal" /> Pick your private fitting with Heather</div>
                    <input data-testid="cart-appointment" type="datetime-local" value={appointment} onChange={(e) => setAppointment(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-rosemetal" />
                  </div>
                )}
                <div>
                  <div className="font-label text-[9px] text-muted-foreground mb-2">Cell Number · Heather texts your confirmation</div>
                  <input data-testid="cart-phone" type="tel" placeholder="(555) 555-5555" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm outline-none focus:border-rosemetal" />
                </div>
                <ul className="space-y-2">
                  {["Locked dresses become permanently yours at " + (schoolName || "your school"), "Steamed, pressed & styled the day you wear it", "Concierge text updates from Heather herself"].map((t) => (
                    <li key={t} className="flex items-start gap-2 font-body text-sm"><Check className="w-4 h-4 text-rosemetal mt-0.5 shrink-0" /> {t}</li>
                  ))}
                </ul>
                <button data-testid="cart-details-continue" disabled={seamstress && !appointment} onClick={() => setStep(2)} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">Review & Secure <ArrowRight className="w-3.5 h-3.5" /></button>
                <button onClick={() => setStep(0)} className="w-full font-label text-[9px] text-muted-foreground flex items-center justify-center gap-1.5"><ArrowLeft className="w-3 h-3" /> Back to bag</button>
              </div>
            )}

            {/* STEP 2 — SECURE */}
            {step === 2 && (
              <div data-testid="cart-step-secure" className="space-y-5">
                <div>
                  <p className="font-label text-[9px] text-rosemetal mb-1">Final step</p>
                  <h3 className="font-display text-3xl">Make it official</h3>
                </div>
                <div className="border border-border rounded-sm p-4 space-y-2 font-body text-sm">
                  {cart.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between"><span className="text-muted-foreground truncate pr-3">{it.product?.name}{it.color ? ` · ${it.color}` : ""}{it.size ? ` · ${it.size}` : ""}</span><span>${it.line_total}</span></div>
                  ))}
                  {extras.garment_bag && <div className="flex justify-between"><span className="text-muted-foreground">Heather's Custom Garment Bag</span><span>${GARMENT_BAG_FEE}</span></div>}
                  {donationAmt > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Donation to {schoolName || "your school"}</span><span>${donationAmt.toFixed(2)}</span></div>}
                  {seamstress && <div className="flex justify-between"><span className="text-muted-foreground">Master Seamstress Suite</span><span>${SEAMSTRESS_FEE}</span></div>}
                  <div className="flex justify-between font-display text-2xl border-t border-border pt-3 mt-2"><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
                </div>

                {!paying ? (
                  <>
                    <button data-testid="cart-pay-btn" onClick={() => setPaying(true)} className="w-full bg-ink text-ivory py-4 font-label text-[10px] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2"><Lock className="w-4 h-4" /> {seamstress ? "Book with Heather & Secure My Look" : "Secure My Look"}</button>
                    <button onClick={() => setStep(1)} className="w-full font-label text-[9px] text-muted-foreground flex items-center justify-center gap-1.5"><ArrowLeft className="w-3 h-3" /> Back to details</button>
                    <p className="font-label text-[8px] text-muted-foreground flex items-center justify-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Encrypted card checkout powered by Stripe</p>
                  </>
                ) : (
                  <div data-testid="cart-payment-element">
                    <PaymentElementForm
                      payload={{
                        kind: "cart",
                        seamstress,
                        appointment: appointment || null,
                        phone: phone || null,
                        garment_bag: !!extras.garment_bag,
                        donation: Number(extras.donation) || 0,
                        donation_school_id: extras.donation_school_id || null,
                      }}
                      cta={`Pay $${total.toFixed(2)} · Secure My Look`}
                      onSuccess={(data) => {
                        onSuccess && onSuccess();
                        try { localStorage.removeItem("hb_cart_extras"); } catch {}
                        if (data?.order_id) { close(); navigate(`/thank-you/${data.order_id}`); }
                      }}
                      onBack={() => setPaying(false)}
                      backLabel="Back to summary"
                      onSchoolVerifyRequired={() => { close(); onSchoolVerifyRequired && onSchoolVerifyRequired(); }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
